import {
  WIKI_AGENT_REPLY_CHOICE_MAX,
  WIKI_AGENT_REPLY_MAX,
  type WikiChangeSet,
  type WikiResolutionReply,
  type WikiThread,
  wikiAgentReplyChoiceSchema,
  wikiAgentReplyTextSchema,
} from "@esi/shared";
import { ArrowLeft, Bot, FileText, Pencil, Send, Undo2, X } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { InlineError, Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Link, seeChangePath, threadInReviewPath } from "../router.tsx";
import { textAreaClass } from "../tasks/parts.tsx";
import { OutcomeChip } from "./AgentReplies.tsx";
import { countsText, useResolutionRun, useResolutionWrite } from "./resolve.ts";
import { useThreads } from "./threads.ts";

const length = (text: string) => Array.from(text.normalize("NFC").trim()).length;

/**
 * A run's replies as its creator validates them (wiki-resolve-with-agent D4; mock "validate"):
 * grouped by theme then page, each with the reviewer's comment, the outcome chip and the draft,
 * editable inline, with Send · Remove; a page header carries Undo this page's changes; Send all
 * sends what is left. Drafts are the creator's only (the run's read is theirs); nothing reaches a
 * reviewer before Send.
 */
export function ResolutionReview(props: {
  projectId: string;
  set: Pick<WikiChangeSet, "id" | "title" | "hiddenItems">;
  runId: string;
  onBack: () => void;
}) {
  const { projectId, set, runId } = props;
  const { t } = useTranslation();
  const id = useId();
  const run = useResolutionRun(projectId, runId, true);
  const threads = useThreads(projectId, set.id);
  const write = useResolutionWrite(projectId, runId, set.id);
  const [undo, setUndo] = useState<{ itemId: string; title: string } | null>(null);
  // Replies being edited: Send all would send their stored text, not the open correction.
  const [editing, setEditing] = useState<ReadonlySet<string>>(new Set());
  const onEditing = (replyId: string, on: boolean) =>
    setEditing((current) => {
      const next = new Set(current);
      if (on) {
        next.add(replyId);
      } else {
        next.delete(replyId);
      }
      return next;
    });
  const back = (
    <Button type="button" size="sm" variant="ghost" className="self-start" onClick={props.onBack}>
      <ArrowLeft aria-hidden="true" strokeWidth={1.75} />
      {t("wiki.resolve.panel.back")}
    </Button>
  );
  if (run.isError) {
    return (
      <div className="flex flex-col gap-3 px-4 py-4 sm:px-5">
        {back}
        <Notice tone="info">{t("wiki.resolve.panel.unavailable")}</Notice>
      </div>
    );
  }
  if (!run.data) {
    return <Skeleton className="m-4 h-40" />;
  }
  const data = run.data;
  const shown = data.replies.filter((r) => r.status !== "removed");
  const drafts = shown.filter((r) => r.status === "draft").length;
  const byThread = new Map((threads.data ?? []).map((th) => [th.id, th]));
  const themes = groupByTheme(shown);
  const covered = new Set(shown.flatMap((r) => (r.itemId ? [r.itemId] : [])));
  const orphans = data.items.filter((i) => !covered.has(i.itemId));
  const title = set.hiddenItems ? "" : set.title;
  return (
    <section
      data-esi-content="wiki"
      aria-labelledby={`${id}-title`}
      data-resolution-review=""
      className="flex flex-col"
    >
      <header className="flex flex-col gap-1.5 border-b border-line px-4 py-3.5 sm:px-5">
        {back}
        <h2
          id={`${id}-title`}
          className="flex items-center gap-2 text-[15px] font-semibold tracking-tight break-words text-fg"
        >
          <Bot aria-hidden="true" className="size-4 shrink-0 text-fg-muted" strokeWidth={1.75} />
          {[
            t("wiki.resolve.revision", { revision: data.revision }),
            ...(title ? [title] : []),
          ].join(" · ")}
        </h2>
        <p className="text-xs text-fg-muted">{t("wiki.resolve.panel.hint")}</p>
        <p className="text-xs text-fg-secondary tabular-nums">{countsText(data.counts)}</p>
      </header>
      {themes.map(([theme, pages]) => (
        <section
          key={theme}
          aria-label={theme}
          className="flex flex-col gap-2 border-b border-line px-4 py-3 sm:px-5"
        >
          <h3 className="text-xs font-medium text-fg-muted">
            {t("wiki.resolve.panel.theme", {
              theme,
              count: pages.reduce((n, [, replies]) => n + replies.length, 0),
            })}
          </h3>
          {pages.map(([key, replies]) => {
            const first = replies[0] as WikiResolutionReply;
            const item = first.itemId ? data.items.find((i) => i.itemId === first.itemId) : null;
            return (
              <div key={key} className="flex flex-col gap-2">
                <div className="flex flex-wrap items-center gap-2 text-[13px] font-medium text-fg">
                  <FileText
                    aria-hidden="true"
                    className="size-3.5 shrink-0 text-fg-muted"
                    strokeWidth={1.75}
                  />
                  <span className="min-w-0 break-words">
                    {first.itemTitle ?? t("wiki.resolve.panel.theSet")}
                  </span>
                  {item?.undone ? (
                    <span className="ml-auto text-xs font-normal text-fg-muted">
                      {t("wiki.resolve.panel.undone")}
                    </span>
                  ) : item?.undoable && data.status !== "running" ? (
                    <Button
                      type="button"
                      size="xs"
                      variant="ghost"
                      className="ml-auto text-fg-secondary"
                      disabled={write.isPending}
                      onClick={() => setUndo({ itemId: item.itemId, title: item.title })}
                    >
                      <Undo2 aria-hidden="true" />
                      {t("wiki.resolve.panel.undoPage")}
                    </Button>
                  ) : null}
                </div>
                <ul className="flex flex-col gap-2">
                  {replies.map((reply) => (
                    <ReplyDraft
                      key={reply.id}
                      projectId={projectId}
                      setId={set.id}
                      reply={reply}
                      thread={byThread.get(reply.threadId)}
                      busy={write.isPending}
                      onEditing={(on) => onEditing(reply.id, on)}
                      onWrite={(w) => write.mutateAsync(w)}
                    />
                  ))}
                </ul>
              </div>
            );
          })}
        </section>
      ))}
      {orphans.length ? (
        // Pages the run changed whose replies are gone (removed, or never drafted): still
        // undoable.
        <section className="flex flex-col gap-2 border-b border-line px-4 py-3 sm:px-5">
          <h3 className="text-xs font-medium text-fg-muted">
            {t("wiki.resolve.panel.otherPages")}
          </h3>
          {orphans.map((item) => (
            <div
              key={item.itemId}
              className="flex flex-wrap items-center gap-2 text-[13px] font-medium text-fg"
            >
              <FileText
                aria-hidden="true"
                className="size-3.5 shrink-0 text-fg-muted"
                strokeWidth={1.75}
              />
              <span className="min-w-0 break-words">{item.title}</span>
              {item.undone ? (
                <span className="ml-auto text-xs font-normal text-fg-muted">
                  {t("wiki.resolve.panel.undone")}
                </span>
              ) : item.undoable && data.status !== "running" ? (
                <Button
                  type="button"
                  size="xs"
                  variant="ghost"
                  className="ml-auto text-fg-secondary"
                  disabled={write.isPending}
                  onClick={() => setUndo({ itemId: item.itemId, title: item.title })}
                >
                  <Undo2 aria-hidden="true" />
                  {t("wiki.resolve.panel.undoPage")}
                </Button>
              ) : null}
            </div>
          ))}
        </section>
      ) : null}
      {data.unanswered.length ? (
        <section className="flex flex-col gap-1.5 border-b border-line px-4 py-3 sm:px-5">
          <h3 className="text-xs font-medium text-fg-muted">
            {t("wiki.resolve.panel.unanswered")}
          </h3>
          <ul className="flex flex-col gap-1">
            {data.unanswered.map((u) => {
              const thread = byThread.get(u.threadId);
              return (
                <li key={u.threadId} className="text-[13px] text-fg-secondary">
                  <Link
                    href={threadInReviewPath(projectId, set.id, thread?.itemId ?? null, u.threadId)}
                    className="text-accent-text underline-offset-4 hover:underline"
                  >
                    {t("wiki.resolve.panel.unansweredItem", {
                      page: u.itemTitle ?? t("wiki.resolve.panel.theSet"),
                    })}
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}
      <footer className="flex flex-wrap items-center gap-2 px-4 py-3 sm:px-5">
        <p className="text-xs text-fg-muted tabular-nums">
          {drafts
            ? t("wiki.resolve.panel.drafts", { count: drafts })
            : t("wiki.resolve.panel.noDrafts")}
        </p>
        {drafts ? (
          <Button
            type="button"
            size="sm"
            className="ml-auto"
            disabled={write.isPending || editing.size > 0}
            onClick={() => write.mutate({ kind: "sendAll" })}
          >
            <Send aria-hidden="true" strokeWidth={1.75} />
            {t("wiki.resolve.sendAll")}
          </Button>
        ) : null}
        {write.isError && !undo ? (
          <div className="basis-full">
            <InlineError>{write.error.message}</InlineError>
          </div>
        ) : null}
      </footer>
      <AppDialog
        open={undo !== null}
        onClose={() => {
          if (!write.isPending) {
            setUndo(null);
            write.reset();
          }
        }}
        title={t("wiki.resolve.panel.undoTitle", { title: undo?.title ?? "" })}
        description={t("wiki.resolve.panel.undoBody")}
      >
        {write.isError ? <InlineError>{write.error.message}</InlineError> : null}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={write.isPending}
            onClick={() => {
              setUndo(null);
              write.reset();
            }}
          >
            {t("app.cancel")}
          </Button>
          <Button
            type="button"
            variant="destructive"
            className="text-accent-on dark:bg-destructive dark:hover:bg-destructive/90"
            disabled={write.isPending}
            onClick={async () => {
              if (!undo) {
                return;
              }
              try {
                await write.mutateAsync({ kind: "undo", itemId: undo.itemId });
                setUndo(null);
              } catch {
                // Shown in the dialog (`item_changed_since`: edit by hand).
              }
            }}
          >
            <Undo2 aria-hidden="true" />
            {t("wiki.resolve.panel.undoConfirm")}
          </Button>
        </DialogFooter>
      </AppDialog>
    </section>
  );
}

/** Replies by theme (first seen first), then by page within a theme. */
function groupByTheme(
  replies: readonly WikiResolutionReply[],
): [string, [string, WikiResolutionReply[]][]][] {
  const themes = new Map<string, Map<string, WikiResolutionReply[]>>();
  for (const reply of replies) {
    const pages = themes.get(reply.theme) ?? new Map<string, WikiResolutionReply[]>();
    const key = reply.itemId ?? "set";
    pages.set(key, [...(pages.get(key) ?? []), reply]);
    themes.set(reply.theme, pages);
  }
  return [...themes].map(([theme, pages]) => [theme, [...pages]]);
}

function ReplyDraft(props: {
  projectId: string;
  setId: string;
  reply: WikiResolutionReply;
  thread: WikiThread | undefined;
  busy: boolean;
  onEditing: (on: boolean) => void;
  onWrite: (
    write: Parameters<ReturnType<typeof useResolutionWrite>["mutateAsync"]>[0],
  ) => Promise<unknown>;
}) {
  const { reply, thread } = props;
  const { t } = useTranslation();
  const id = useId();
  const [editing, setEditingState] = useState(false);
  const setEditing = (on: boolean) => {
    setEditingState(on);
    props.onEditing(on);
  };
  // A reply that leaves the list (an undone page) no longer holds Send all.
  const onEditing = useRef(props.onEditing);
  onEditing.current = props.onEditing;
  useEffect(() => () => onEditing.current(false), []);
  const [text, setText] = useState(reply.text);
  const [choices, setChoices] = useState<string[]>(reply.choices);
  const [error, setError] = useState<string | null>(null);
  const opener = thread?.comments.find((c) => !c.system);
  const name = thread?.createdByName ?? t("wiki.resolve.reply.someone");
  const draft = reply.status === "draft";
  const save = async () => {
    const body = wikiAgentReplyTextSchema.safeParse(text);
    const picked = choices.map((c) => c.trim()).filter(Boolean);
    const parsedChoices = picked.map((c) => wikiAgentReplyChoiceSchema.safeParse(c));
    if (!body.success || parsedChoices.some((c) => !c.success)) {
      setError(t("wiki.threads.invalid"));
      return;
    }
    setError(null);
    try {
      await props.onWrite({
        kind: "edit",
        replyId: reply.id,
        request: {
          text: body.data,
          ...(reply.outcome === "question" && { choices: picked }),
        },
      });
      setEditing(false);
    } catch {
      // The list shows the refusal.
    }
  };
  return (
    <li
      data-agent-reply-draft={reply.status}
      className="flex flex-col gap-1.5 rounded-md border border-line bg-surface-raised px-3 py-2.5"
    >
      <p className="text-xs text-fg-muted">
        {t("wiki.resolve.panel.from", {
          name,
          kind: thread ? t(`wiki.threads.kind.${thread.kind}`).toLowerCase() : "",
        })}
        {thread?.anchor?.quote
          ? ` · ${t("wiki.resolve.panel.on", { quote: thread.anchor.quote })}`
          : ""}
      </p>
      {opener && !opener.deleted ? (
        <p className="text-[13px] whitespace-pre-wrap break-words text-fg-secondary">
          {opener.body}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        <OutcomeChip outcome={reply.outcome} name={name} />
        {reply.status === "sent" ? (
          <span className="text-xs text-fg-muted">{t("wiki.resolve.panel.sent")}</span>
        ) : null}
      </div>
      {editing ? (
        <form
          className="flex flex-col gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <Label htmlFor={`${id}-text`} className="sr-only">
            {t("wiki.resolve.panel.editLabel", { name })}
          </Label>
          <textarea
            id={`${id}-text`}
            value={text}
            onChange={(e) => setText(e.target.value)}
            className={`${textAreaClass} min-h-20`}
            disabled={props.busy}
            aria-describedby={`${id}-count`}
            aria-invalid={error ? true : undefined}
          />
          <p id={`${id}-count`} className="text-xs text-fg-muted tabular-nums">
            {length(text)}/{WIKI_AGENT_REPLY_MAX}
          </p>
          {reply.outcome === "question"
            ? choices.map((choice, n) => (
                <div key={`${reply.id}-${n.toString()}`} className="flex flex-col gap-1">
                  <Label htmlFor={`${id}-choice-${n}`} className="text-xs text-fg-muted">
                    {t("wiki.resolve.panel.choiceLabel", { n: n + 1 })}
                  </Label>
                  <Input
                    id={`${id}-choice-${n}`}
                    value={choice}
                    maxLength={WIKI_AGENT_REPLY_CHOICE_MAX}
                    disabled={props.busy}
                    onChange={(e) => {
                      const value = e.target.value;
                      setChoices((list) => list.map((c, i) => (i === n ? value : c)));
                    }}
                  />
                </div>
              ))
            : null}
          {error ? <InlineError>{error}</InlineError> : null}
          <div className="flex gap-1.5">
            <Button type="submit" size="xs" variant="outline" disabled={props.busy}>
              {t("wiki.resolve.panel.save")}
            </Button>
            <Button
              type="button"
              size="xs"
              variant="ghost"
              disabled={props.busy}
              onClick={() => {
                setEditing(false);
                setText(reply.text);
                setChoices(reply.choices);
                setError(null);
              }}
            >
              {t("app.cancel")}
            </Button>
          </div>
        </form>
      ) : (
        <>
          <p className="rounded-md border border-line-strong bg-surface px-2.5 py-2 text-[13px] whitespace-pre-wrap break-words text-fg">
            {reply.text}
          </p>
          {reply.choices.length ? (
            <p className="text-xs text-fg-muted">
              {t("wiki.resolve.panel.choices", { list: reply.choices.join(" · ") })}
            </p>
          ) : null}
        </>
      )}
      {draft && !editing ? (
        <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
          <Button
            type="button"
            size="xs"
            variant="outline"
            disabled={props.busy}
            onClick={() => void props.onWrite({ kind: "send", replyId: reply.id }).catch(() => {})}
          >
            <Send aria-hidden="true" />
            {t("wiki.resolve.panel.send")}
          </Button>
          <Button
            type="button"
            size="xs"
            variant="ghost"
            disabled={props.busy}
            onClick={() => setEditing(true)}
          >
            <Pencil aria-hidden="true" />
            {t("wiki.resolve.panel.edit")}
          </Button>
          <Button
            type="button"
            size="xs"
            variant="ghost"
            disabled={props.busy}
            onClick={() =>
              void props.onWrite({ kind: "remove", replyId: reply.id }).catch(() => {})
            }
          >
            <X aria-hidden="true" />
            {t("wiki.resolve.panel.remove")}
          </Button>
          {reply.outcome === "changed" && reply.itemId ? (
            <Link
              href={seeChangePath(props.projectId, props.setId, reply.itemId, reply.threadId)}
              className="ml-auto text-xs text-accent-text underline-offset-4 hover:underline"
            >
              {t("wiki.resolve.reply.seeChange")}
            </Link>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}
